import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen, Plus, Edit, Search, Eye, CheckCircle,
  Download, Tag, AlertTriangle, Archive, History, FileText,
  Shield, Building2, Wrench, Award, Trash2, X, Sparkles, RotateCcw,
  ShieldCheck, Clock, XCircle, Send
} from "lucide-react";
import WysiwygEditor from "./WysiwygEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPI = [
  { key: 'sanitaria', label: 'Sanitaria', prefix: 'SAN', icon: Shield, color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-200' },
  { key: 'amministrativa', label: 'Amministrativa', prefix: 'AMM', icon: Building2, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  { key: 'tecnica', label: 'Tecnica Assistenziale', prefix: 'PTA', icon: Wrench, color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200' },
  { key: 'privacy', label: 'Privacy/Sicurezza', prefix: 'PRI', icon: Shield, color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  { key: 'qualita', label: 'Qualità', prefix: 'QUA', icon: Award, color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200' },
];

const RUOLI_OPTS = [
  { key: 'operatore', label: 'Operatore' },
  { key: 'amministratore', label: 'Amministratore' },
  { key: 'coordinatore', label: 'Coordinatore' },
  { key: 'direzione', label: 'Direzione' },
];
const FIGURE_OPTS = ['Infermiere', 'OSS', 'Fisioterapista', 'Medico', 'Amministrativo'];
const ASL_OPTS = ['ASL Pescara', 'ASL Chieti', "ASL L'Aquila", 'ASL Teramo'];

const TEMPLATE_HTML = `<h1>Titolo Procedura</h1>
<h2>PREMESSA</h2>
<p>Descrivere il contesto generale della procedura.</p>
<h2>OGGETTO</h2>
<p>Indicare l'attività o il processo disciplinato dalla procedura.</p>
<h2>SCOPO</h2>
<ul>
<li>Garantire uniformità operativa.</li>
<li>Assicurare sicurezza e tracciabilità.</li>
<li>Definire responsabilità e modalità operative.</li>
</ul>
<h2>CAMPO DI APPLICAZIONE</h2>
<p>Indicare a quali operatori, servizi o reparti si applica la procedura.</p>
<h2>RESPONSABILITÀ</h2>
<table><tbody>
<tr><th>Figura</th><th>Responsabilità</th></tr>
<tr><td>Direzione</td><td>Approva la procedura.</td></tr>
<tr><td>Responsabile</td><td>Verifica e coordina l'applicazione.</td></tr>
<tr><td>Operatori</td><td>Applicano la procedura.</td></tr>
</tbody></table>
<h2>MATERIALI / DOCUMENTI NECESSARI</h2>
<ul><li>Inserire materiali, moduli o documenti necessari.</li></ul>
<h2>PROCEDURA OPERATIVA</h2>
<ol>
<li>Descrivere la prima fase operativa.</li>
<li>Descrivere la seconda fase operativa.</li>
<li>Descrivere le verifiche da effettuare.</li>
</ol>
<h2>GESTIONE ANOMALIE / NON CONFORMITÀ</h2>
<p>Descrivere cosa fare in caso di errore, criticità o mancato rispetto della procedura.</p>
<h2>PRIVACY E SICUREZZA</h2>
<p>Indicare le misure di riservatezza, sicurezza e protezione dei dati.</p>
<h2>REGISTRAZIONI</h2>
<p>Indicare dove vengono registrate le attività svolte.</p>
<h2>INDICATORI DI CONTROLLO</h2>
<ul>
<li>Numero non conformità.</li>
<li>Tempi di gestione.</li>
<li>Completezza documentale.</li>
</ul>
<h2>RIFERIMENTI</h2>
<ul>
<li>Normativa vigente.</li>
<li>Procedure interne collegate.</li>
<li>Linee guida applicabili.</li>
</ul>`;

const emptyForm = {
  titolo: '', categoria_id: '', tipo: '', contenuto: '', versione: '1.0',
  motivo_revisione: '',
  target_roles: [], target_figure: [], target_asl: [], is_mandatory: false,
  verificatori_ids: ['', '', ''],
  verificatori_titoli: ['', '', ''],
  import_approvato: false,
  data_approvazione: '', approvatore: '', ruolo_approvatore: '',
};

export default function Procedure({ user }) {
  const [procedures, setProcedures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableVerifiers, setAvailableVerifiers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyNote, setVerifyNote] = useState('');
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishForm, setPublishForm] = useState({ visibility: 'targeted', target_roles: [], target_figure: [], target_asl: [] });
  const [showSendVerifyDialog, setShowSendVerifyDialog] = useState(false);
  const [sendVerifyForm, setSendVerifyForm] = useState({ verificatori_ids: ['', '', ''], verificatori_titoli: ['', '', ''] });

  const [editingProc, setEditingProc] = useState(null);
  const [selectedProc, setSelectedProc] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [importingPdf, setImportingPdf] = useState(false);

  const [catForm, setCatForm] = useState({ nome: '', descrizione: '', ordine: 0 });
  const [editingCat, setEditingCat] = useState(null);

  const isSuperAdmin = user.role === 'super_amministratore';
  const isAdmin = isSuperAdmin || user.role === 'amministratore';
  const isEditor = user.has_procedure_editor_access === true;
  const canCreate = isAdmin || isEditor;

  const fetchAll = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const requests = [
        axios.get(`${API}/procedure`, { headers }),
        axios.get(`${API}/procedure/categorie`, { headers }),
      ];
      if (canCreate) requests.push(axios.get(`${API}/procedure/verificatori-disponibili`, { headers }));
      const results = await Promise.all(requests);
      setProcedures(results[0].data || []);
      setCategories(results[1].data || []);
      if (canCreate && results[2]) setAvailableVerifiers(results[2].data || []);
    } catch (error) {
      console.error('Errore caricamento procedure:', error);
    }
  }, [canCreate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const hasRead = useCallback((p) => (p.reads || []).some(r => r.user_id === user.id && r.versione === p.versione), [user.id]);
  const needsReread = useCallback((p) => {
    const myRead = (p.reads || []).find(r => r.user_id === user.id);
    return myRead && myRead.versione !== p.versione;
  }, [user.id]);

  const isPublished = useCallback((p) => (p.stato_workflow) === 'pubblicato', []);
  const myPendingVerify = useCallback((p) => {
    if (p.stato_workflow !== 'in_verifica') return false;
    const mine = (p.verificatori || []).find(v => v.user_id === user.id);
    return !!mine && mine.stato === 'pending';
  }, [user.id]);

  // Procedures awaiting my verification
  const pendingVerifications = useMemo(
    () => procedures.filter(p => myPendingVerify(p)),
    [procedures, myPendingVerify]
  );

  // Visible procedures (active, unless showing archived)
  const visibleProcs = useMemo(
    () => procedures.filter(p => showArchived ? true : p.is_active !== false),
    [procedures, showArchived]
  );

  const stats = useMemo(() => {
    const active = procedures.filter(p => p.is_active !== false && isPublished(p));
    return {
      totale: active.length,
      obbligatorie: active.filter(p => p.is_mandatory).length,
      daLeggere: active.filter(p => !hasRead(p)).length,
      lette: active.filter(p => hasRead(p)).length,
      sanitarie: active.filter(p => p.tipo === 'sanitaria').length,
      amministrative: active.filter(p => p.tipo === 'amministrativa').length,
    };
  }, [procedures, hasRead, isPublished]);

  const filtered = useMemo(() => visibleProcs.filter(p => {
    const matchSearch = !search ||
      p.titolo.toLowerCase().includes(search.toLowerCase()) ||
      (p.codice || '').toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || p.categoria_id === filterCat;
    const matchTipo = !filterTipo || p.tipo === filterTipo;
    const read = hasRead(p);
    let matchStato = true;
    if (filterStato === 'da_leggere') matchStato = !read && isPublished(p);
    else if (filterStato === 'lette') matchStato = read;
    else if (filterStato === 'obbligatorie') matchStato = p.is_mandatory && isPublished(p);
    else if (filterStato === 'in_verifica') matchStato = p.stato_workflow === 'in_verifica';
    else if (filterStato === 'approvato') matchStato = p.stato_workflow === 'approvato';
    else if (filterStato === 'bozza') matchStato = p.stato_workflow === 'bozza';
    return matchSearch && matchCat && matchTipo && matchStato;
  }), [visibleProcs, search, filterCat, filterTipo, filterStato, hasRead, isPublished]);

  const catName = useCallback((id) => categories.find(c => c.id === id)?.nome || '—', [categories]);
  const tipoInfo = useCallback((key) => TIPI.find(t => t.key === key), []);

  // --- Procedure CRUD ---
  const handleSave = async (azione = 'salva_privato') => {
    if (!form.titolo.trim()) return toast.error('Titolo obbligatorio');
    if (!form.tipo) return toast.error('Tipo procedura obbligatorio');
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    if (editingProc) {
      try {
        const { verificatori_ids, verificatori_titoli, import_approvato, ...rest } = form;
        const payload = { ...rest };
        if (form.versione === editingProc.versione) delete payload.motivo_revisione;
        await axios.put(`${API}/procedure/${editingProc.id}`, payload, { headers });
        toast.success('Procedura aggiornata');
        setShowForm(false); setEditingProc(null); setForm(emptyForm); fetchAll();
      } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
      return;
    }

    let cleanVerifIds = [], cleanVerifTitoli = [];
    if (azione === 'invia_verifica') {
      cleanVerifIds = form.verificatori_ids.filter(id => id);
      if (cleanVerifIds.length !== 3) return toast.error('Devi selezionare esattamente 3 verificatori');
      if (new Set(cleanVerifIds).size !== 3) return toast.error('I 3 verificatori devono essere diversi');
      cleanVerifTitoli = (form.verificatori_titoli || []).slice(0, 3);
      while (cleanVerifTitoli.length < 3) cleanVerifTitoli.push('');
    }
    if (azione === 'importa_approvato') {
      if (!form.data_approvazione) return toast.error('Indica la data di approvazione');
      if (!form.approvatore?.trim()) return toast.error("Indica l'approvatore");
      if (!form.ruolo_approvatore?.trim()) return toast.error("Indica il ruolo dell'approvatore");
      if (!form.versione?.trim()) return toast.error('Indica la versione');
    }

    try {
      const { motivo_revisione, verificatori_ids, verificatori_titoli, import_approvato, ...rest } = form;
      const payload = { ...rest, azione };
      if (azione === 'invia_verifica') {
        payload.verificatori_ids = cleanVerifIds;
        payload.verificatori_titoli = cleanVerifTitoli;
      }
      await axios.post(`${API}/procedure`, payload, { headers });
      toast.success(
        azione === 'invia_verifica' ? 'Procedura inviata ai verificatori'
          : azione === 'importa_approvato' ? 'Documento salvato come approvato (privato)'
            : 'Bozza salvata in privato'
      );
      setShowForm(false); setEditingProc(null); setForm(emptyForm); fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handlePublish = async () => {
    if (publishForm.visibility === 'targeted' &&
        !(publishForm.target_roles.length || publishForm.target_figure.length || publishForm.target_asl.length)) {
      return toast.error('Seleziona almeno un destinatario');
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/procedure/${selectedProc.id}/pubblica`, publishForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Procedura pubblicata');
      setShowPublishDialog(false); setShowDetail(false);
      setPublishForm({ visibility: 'targeted', target_roles: [], target_figure: [], target_asl: [] });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore pubblicazione'); }
  };

  const handleSendVerify = async () => {
    const ids = sendVerifyForm.verificatori_ids.filter(x => x);
    if (ids.length !== 3) return toast.error('Seleziona esattamente 3 verificatori');
    if (new Set(ids).size !== 3) return toast.error('I 3 verificatori devono essere diversi');
    const titoli = (sendVerifyForm.verificatori_titoli || []).slice(0, 3);
    while (titoli.length < 3) titoli.push('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/procedure/${selectedProc.id}/invia-verifica`,
        { verificatori_ids: ids, verificatori_titoli: titoli },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Procedura inviata in verifica');
      setShowSendVerifyDialog(false); setShowDetail(false);
      setSendVerifyForm({ verificatori_ids: ['', '', ''], verificatori_titoli: ['', '', ''] });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio'); }
  };

  const handleVerify = async (approvato) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/procedure/${selectedProc.id}/verifica`,
        { approvato, note: verifyNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approvato ? 'Verifica approvata' : 'Verifica rifiutata');
      setShowVerifyDialog(false);
      setVerifyNote('');
      setShowDetail(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore registrazione verifica');
    }
  };

  const handleArchive = async (proc) => {
    if (!window.confirm(`Archiviare la procedura "${proc.titolo}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/procedure/${proc.id}/archivia`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Procedura archiviata');
      fetchAll();
    } catch (error) {
      toast.error('Errore archiviazione');
    }
  };

  const handleRestore = async (proc) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/procedure/${proc.id}/ripristina`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Procedura ripristinata');
      fetchAll();
    } catch (error) {
      toast.error('Errore ripristino');
    }
  };

  const handleDelete = async (proc) => {
    if (!window.confirm(`ELIMINARE definitivamente "${proc.titolo}"? Operazione non reversibile.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/procedure/${proc.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Procedura eliminata');
      fetchAll();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const handleConfirmRead = async (proc) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/procedure/${proc.id}/conferma-lettura`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Presa visione confermata');
      fetchAll();
    } catch (error) {
      toast.error('Errore conferma lettura');
    }
  };

  const handleDownloadPDF = async (proc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/procedure/${proc.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${proc.codice || 'Procedura'}_${proc.titolo.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF scaricato');
    } catch (error) {
      toast.error('Errore download PDF');
    }
  };

  // --- Cat CRUD ---
  const handleSaveCat = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingCat) {
        await axios.put(`${API}/procedure/categorie/${editingCat.id}`, catForm, { headers });
        toast.success('Categoria aggiornata');
      } else {
        await axios.post(`${API}/procedure/categorie`, catForm, { headers });
        toast.success('Categoria creata');
      }
      setCatForm({ nome: '', descrizione: '', ordine: 0 });
      setEditingCat(null);
      fetchAll();
    } catch (error) {
      toast.error('Errore salvataggio categoria');
    }
  };

  const handleDeleteCat = async (cat) => {
    if (!window.confirm(`Eliminare categoria "${cat.nome}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/procedure/categorie/${cat.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Categoria eliminata');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const openEdit = (proc) => {
    setEditingProc(proc);
    setForm({
      titolo: proc.titolo || '',
      categoria_id: proc.categoria_id || '',
      tipo: proc.tipo || '',
      contenuto: proc.contenuto || '',
      versione: proc.versione || '1.0',
      motivo_revisione: '',
      target_roles: proc.target_roles || [],
      target_figure: proc.target_figure || [],
      target_asl: proc.target_asl || [],
      is_mandatory: proc.is_mandatory || false,
    });
    setShowForm(true);
  };

  const openNew = () => {
    setEditingProc(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const toggleArr = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  const handleImportPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset cosi' lo stesso file puo' essere ri-selezionato
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seleziona un file PDF');
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error('PDF troppo grande (max 30 MB)');
      return;
    }
    if (form.contenuto && form.contenuto.replace(/<[^>]+>/g, '').trim().length > 50) {
      if (!window.confirm('L\'editor contiene gia\' testo. Importare il PDF SOSTITUIRA\' tutto il contenuto attuale. Procedere?')) {
        return;
      }
    }
    setImportingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/procedure/import-pdf`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { html, suggested_title, page_count, image_count, warnings } = res.data || {};
      setForm(prev => ({
        ...prev,
        contenuto: html || prev.contenuto,
        // popola titolo solo se vuoto
        titolo: prev.titolo || suggested_title || prev.titolo,
      }));
      const warnTxt = (warnings && warnings.length) ? ` (${warnings.join('; ')})` : '';
      toast.success(`PDF importato: ${page_count} pagine, ${image_count} immagini${warnTxt}. Verifica il contenuto e salva.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore importazione PDF');
    } finally {
      setImportingPdf(false);
    }
  };

  // ---------- RENDER ----------
  return (
    <div className="space-y-5" data-testid="procedure-module">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-cyan-600" />
            Procedure
          </h2>
          <p className="text-xs text-gray-500">Protocolli operativi e gestionali</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => setShowCatDialog(true)}
                className="h-8 gap-1.5 text-xs border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="btn-manage-categories">
                <Tag className="w-3.5 h-3.5" /> Categorie
              </Button>
            )}
            <Button size="sm" onClick={openNew}
              className="h-8 gap-1.5 text-xs bg-cyan-600 hover:bg-cyan-700" data-testid="btn-new-procedure">
              <Plus className="w-3.5 h-3.5" /> Nuova Procedura
            </Button>
          </div>
        )}
      </div>

      {/* Banner: procedure da verificare */}
      {pendingVerifications.length > 0 && (
        <button
          type="button"
          onClick={() => { setFilterStato('in_verifica'); }}
          className="w-full flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors text-left"
          data-testid="verification-banner"
        >
          <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-800">
              {pendingVerifications.length === 1
                ? '1 procedura da verificare'
                : `${pendingVerifications.length} procedure da verificare`}
            </div>
            <div className="text-xs text-amber-700 truncate">
              {pendingVerifications.slice(0, 2).map(p => p.titolo).join(' · ')}
              {pendingVerifications.length > 2 && ` · +${pendingVerifications.length - 2} altre`}
            </div>
          </div>
        </button>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="procedure-stats">
        <StatCard label="Totale" value={stats.totale} color="text-gray-900" bg="bg-white" />
        <StatCard label="Obbligatorie" value={stats.obbligatorie} color="text-red-600" bg="bg-red-50/50" />
        <StatCard label="Da leggere" value={stats.daLeggere} color="text-amber-600" bg="bg-amber-50/50" />
        <StatCard label="Lette" value={stats.lette} color="text-emerald-600" bg="bg-emerald-50/50" />
        <StatCard label="Sanitarie" value={stats.sanitarie} color="text-cyan-700" bg="bg-cyan-50/50" />
        <StatCard label="Amministrative" value={stats.amministrative} color="text-blue-700" bg="bg-blue-50/50" />
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Cerca per titolo o codice..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm" data-testid="procedure-search" />
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          {[
            { key: '', label: 'Tutte', count: visibleProcs.length },
            { key: 'da_leggere', label: 'Da leggere', count: stats.daLeggere },
            { key: 'lette', label: 'Lette', count: stats.lette },
            { key: 'obbligatorie', label: 'Obbligatorie', count: stats.obbligatorie },
            ...(canCreate ? [
              { key: 'in_verifica', label: 'In verifica', count: visibleProcs.filter(p => p.stato_workflow === 'in_verifica').length },
              { key: 'approvato', label: 'Approvate', count: visibleProcs.filter(p => p.stato_workflow === 'approvato').length },
              { key: 'bozza', label: 'Bozze', count: visibleProcs.filter(p => p.stato_workflow === 'bozza').length },
            ] : []),
          ].map(f => (
            <FilterPill key={f.key || 'all'} active={filterStato === f.key} onClick={() => setFilterStato(f.key)}>
              {f.label} ({f.count})
            </FilterPill>
          ))}
          <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}
            className="h-7 px-2 text-xs rounded-full border border-gray-200 bg-white text-gray-600" data-testid="filter-tipo">
            <option value="">Tutti i tipi</option>
            {TIPI.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          {categories.length > 0 && (
            <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)}
              className="h-7 px-2 text-xs rounded-full border border-gray-200 bg-white text-gray-600" data-testid="filter-categoria">
              <option value="">Tutte le categorie</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          )}
          {isAdmin && (
            <button onClick={() => setShowArchived(s => !s)}
              className={`ml-auto px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                showArchived ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="toggle-archived">
              <Archive className="w-3.5 h-3.5 inline mr-1" /> {showArchived ? 'Nascondi archiviate' : 'Mostra archiviate'}
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
          <BookOpen className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">{visibleProcs.length === 0 ? 'Nessuna procedura pubblicata' : 'Nessun risultato'}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="procedure-list">
          {filtered.map(proc => (
            <ProcedureRow key={proc.id} proc={proc} isAdmin={isAdmin}
              read={hasRead(proc)} reread={needsReread(proc)}
              catName={catName(proc.categoria_id)} tipoInfo={tipoInfo(proc.tipo)}
              onView={() => { setSelectedProc(proc); setShowDetail(true); }}
              onEdit={() => openEdit(proc)}
              onArchive={() => handleArchive(proc)}
              onRestore={() => handleRestore(proc)}
              onDelete={() => handleDelete(proc)}
              onPDF={() => handleDownloadPDF(proc)} />
          ))}
        </div>
      )}

      {/* Detail */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
          {selectedProc && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <DialogTitle className="text-base flex items-center gap-2">
                      <span>{selectedProc.titolo}</span>
                      {selectedProc.codice && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-mono rounded">{selectedProc.codice}</span>}
                    </DialogTitle>
                    <DialogDescription className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">{catName(selectedProc.categoria_id)}</span>
                      {tipoInfo(selectedProc.tipo) && (
                        <span className={`px-1.5 py-0.5 ${tipoInfo(selectedProc.tipo).bg} ${tipoInfo(selectedProc.tipo).color} rounded text-[10px] font-medium`}>
                          {tipoInfo(selectedProc.tipo).label}
                        </span>
                      )}
                      <span className="text-[11px] text-gray-500">v{selectedProc.versione}</span>
                      {selectedProc.is_mandatory && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-medium">Obbligatoria</span>}
                      {selectedProc.is_active === false && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded text-[10px] font-medium">Archiviata</span>}
                      {selectedProc.stato_workflow === 'in_verifica' && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium flex items-center gap-1">
                          <Clock className="w-3 h-3" /> In verifica
                        </span>
                      )}
                      {selectedProc.stato_workflow === 'bozza' && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium">Bozza (privata)</span>
                      )}
                      {selectedProc.stato_workflow === 'approvato' && (
                        <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Approvato (privato)
                        </span>
                      )}
                      {selectedProc.stato_workflow === 'pubblicato' && (
                        <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded text-[10px] font-medium">
                          Pubblicato · {selectedProc.visibility === 'all' ? 'Tutti' : 'Destinatari selezionati'}
                        </span>
                      )}
                    </DialogDescription>
                  </div>
                  {selectedProc.storico_versioni?.length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => setShowHistory(true)}
                      className="h-7 text-xs gap-1" data-testid="btn-show-history">
                      <History className="w-3.5 h-3.5" /> Storico
                    </Button>
                  )}
                </div>
              </DialogHeader>

              {/* Meta info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 p-3 bg-gray-50 rounded-lg text-xs">
                <MetaField label="Data creazione" value={selectedProc.created_at ? new Date(selectedProc.created_at).toLocaleDateString('it-IT') : '—'} />
                <MetaField label="Ultima revisione" value={selectedProc.last_revision_at ? new Date(selectedProc.last_revision_at).toLocaleDateString('it-IT') : '—'} />
                <MetaField label="Autore" value={selectedProc.created_by_name || '—'} />
                <MetaField label="N. letture" value={selectedProc.reads?.length || 0} />
              </div>

              <div className="procedure-content mt-4" dangerouslySetInnerHTML={{ __html: selectedProc.contenuto || '' }} />

              {/* Workflow verification block */}
              {selectedProc.verificatori?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100" data-testid="verifiers-block">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" /> Verifica e Approvazione
                  </h4>
                  <div className="space-y-1.5">
                    {selectedProc.verificatori.map(v => {
                      let stateColor = 'bg-gray-100 text-gray-500';
                      let stateLabel = 'In attesa';
                      let Icon = Clock;
                      if (v.stato === 'approvato') { stateColor = 'bg-emerald-100 text-emerald-700'; stateLabel = 'Approvata'; Icon = CheckCircle; }
                      else if (v.stato === 'rifiutato') { stateColor = 'bg-red-100 text-red-700'; stateLabel = 'Rifiutata'; Icon = XCircle; }
                      return (
                        <div key={v.user_id} className="flex items-start gap-2 p-2 rounded border border-gray-100 bg-white">
                          <div className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 shrink-0 ${stateColor}`}>
                            <Icon className="w-3 h-3" /> {stateLabel}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-700">{v.user_name}</div>
                            {v.note && (
                              <div className="text-[11px] text-gray-500 italic mt-0.5">"{v.note}"</div>
                            )}
                          </div>
                          {v.data_decisione && (
                            <div className="text-[10px] text-gray-400 shrink-0">
                              {new Date(v.data_decisione).toLocaleDateString('it-IT')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {isAdmin && selectedProc.reads?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Prese visione ({selectedProc.reads.length})
                  </h4>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {selectedProc.reads.map(r => (
                      <div key={`${r.user_id}-${r.versione}-${r.read_at}`}
                        className="flex items-center justify-between text-xs text-gray-600 py-1.5 px-2 rounded hover:bg-gray-50">
                        <span className="font-medium">{r.user_name}</span>
                        <span className="text-gray-400">
                          v{r.versione} — {new Date(r.read_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="mt-4 gap-2 flex-wrap">
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedProc)} className="gap-1.5" data-testid="btn-download-pdf">
                  <Download className="w-4 h-4" /> Scarica PDF
                </Button>
                {/* Verification actions - visible to verifiers with pending vote */}
                {myPendingVerify(selectedProc) && (
                  <>
                    <Button onClick={() => { setVerifyNote(''); setShowVerifyDialog(true); }}
                      variant="outline"
                      className="gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="btn-open-verify">
                      <ShieldCheck className="w-4 h-4" /> Approva / Rifiuta
                    </Button>
                  </>
                )}
                {/* Bozza -> invia in verifica (autore/admin) */}
                {selectedProc.stato_workflow === 'bozza' && (selectedProc.created_by === user.id || isAdmin) && (
                  <Button onClick={() => { setSendVerifyForm({ verificatori_ids: ['', '', ''], verificatori_titoli: ['', '', ''] }); setShowSendVerifyDialog(true); }}
                    variant="outline"
                    className="gap-1.5 border-cyan-200 text-cyan-700 hover:bg-cyan-50" data-testid="btn-open-send-verify">
                    <Send className="w-4 h-4" /> Invia in verifica
                  </Button>
                )}
                {/* Approvato -> pubblica (solo Super Admin) */}
                {selectedProc.stato_workflow === 'approvato' && isSuperAdmin && (
                  <Button onClick={() => { setPublishForm({ visibility: 'targeted', target_roles: selectedProc.target_roles || [], target_figure: selectedProc.target_figure || [], target_asl: selectedProc.target_asl || [] }); setShowPublishDialog(true); }}
                    className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="btn-open-publish">
                    <CheckCircle className="w-4 h-4" /> Pubblica
                  </Button>
                )}
                {/* Read confirmation - only for published procedures */}
                {isPublished(selectedProc) && (!hasRead(selectedProc) ? (
                  <Button onClick={() => { handleConfirmRead(selectedProc); setShowDetail(false); }}
                    className="bg-cyan-600 hover:bg-cyan-700 gap-1.5" data-testid="btn-confirm-read">
                    <CheckCircle className="w-4 h-4" /> Ho letto e compreso la procedura
                  </Button>
                ) : (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-md">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-xs font-medium text-emerald-700">Presa visione confermata</span>
                  </div>
                ))}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4 text-cyan-600" /> Storico versioni</DialogTitle>
            <DialogDescription>
              {selectedProc?.titolo} ({selectedProc?.codice}) · versione corrente v{selectedProc?.versione}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {(!selectedProc?.storico_versioni || selectedProc.storico_versioni.length === 0) ? (
              <p className="text-sm text-gray-400 text-center py-6">Nessuna revisione precedente</p>
            ) : (
              [...selectedProc.storico_versioni].reverse().map((h, idx) => (
                <div key={`${h.versione}-${h.updated_at}-${idx}`}
                  className="p-3 rounded-lg border border-gray-200 bg-gray-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs font-mono text-gray-700">v{h.versione}</span>
                      <span className="text-xs text-gray-500">{h.updated_at ? new Date(h.updated_at).toLocaleString('it-IT') : ''}</span>
                    </div>
                    <span className="text-xs text-gray-500">di {h.autore_revisione || '—'}</span>
                  </div>
                  {h.motivo_revisione && (
                    <div className="text-xs text-gray-700 mb-2">
                      <strong className="text-gray-500">Motivo: </strong>{h.motivo_revisione}
                    </div>
                  )}
                  <details className="text-xs">
                    <summary className="cursor-pointer text-cyan-600 hover:text-cyan-700">Mostra contenuto v{h.versione}</summary>
                    <div className="mt-2 p-3 bg-white rounded border border-gray-200 procedure-content"
                      dangerouslySetInnerHTML={{ __html: h.contenuto || '<em>(vuoto)</em>' }} />
                  </details>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setEditingProc(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingProc ? (
                <><Edit className="w-4 h-4 text-cyan-600" /> Modifica Procedura</>
              ) : (
                <><Plus className="w-4 h-4 text-cyan-600" /> Nuova Procedura</>
              )}
            </DialogTitle>
            {editingProc?.codice && (
              <DialogDescription>
                Codice: <span className="font-mono font-semibold text-gray-700">{editingProc.codice}</span>
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Row 1: title + template button */}
            <div className="flex gap-2 items-end">
              <div className="space-y-1.5 flex-1">
                <Label className="text-xs">Titolo *</Label>
                <Input value={form.titolo} onChange={(e) => setForm({...form, titolo: e.target.value})}
                  placeholder="Titolo procedura" className="h-9" data-testid="input-titolo" />
              </div>
              <Button type="button" variant="outline"
                onClick={async () => {
                  if (form.contenuto && !window.confirm('Il contenuto verrà sostituito con il template. Continuare?')) return;
                  try {
                    const token = localStorage.getItem('token');
                    const res = await axios.get(`${API}/procedure-template`, { headers: { Authorization: `Bearer ${token}` } });
                    setForm(prev => ({ ...prev, contenuto: res.data.html || TEMPLATE_HTML }));
                  } catch { setForm(prev => ({ ...prev, contenuto: TEMPLATE_HTML })); }
                }}
                className="h-9 gap-1.5 text-xs border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                data-testid="btn-use-template">
                <Sparkles className="w-3.5 h-3.5" /> Usa Template
              </Button>
            </div>

            {/* Row 2: tipo, categoria, versione */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo procedura *</Label>
                <select value={form.tipo} onChange={(e) => setForm({...form, tipo: e.target.value})}
                  className="w-full h-9 px-3 text-sm rounded-md border border-gray-200 bg-white" data-testid="select-tipo">
                  <option value="">— Seleziona —</option>
                  {TIPI.map(t => <option key={t.key} value={t.key}>{t.label} ({t.prefix}_###)</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Categoria</Label>
                <select value={form.categoria_id} onChange={(e) => setForm({...form, categoria_id: e.target.value})}
                  className="w-full h-9 px-3 text-sm rounded-md border border-gray-200 bg-white" data-testid="select-categoria">
                  <option value="">Nessuna categoria</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Versione</Label>
                <Input value={form.versione} onChange={(e) => setForm({...form, versione: e.target.value})}
                  placeholder="1.0" className="h-9" data-testid="input-versione" />
              </div>
            </div>

            {/* Motivo revisione (only when editing + version changed) */}
            {editingProc && form.versione !== editingProc.versione && (
              <div className="space-y-1.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Label className="text-xs text-amber-800 font-semibold">Motivo revisione * (v{editingProc.versione} → v{form.versione})</Label>
                <Input value={form.motivo_revisione} onChange={(e) => setForm({...form, motivo_revisione: e.target.value})}
                  placeholder="Descrivi brevemente cosa è cambiato..." className="h-9 bg-white" data-testid="input-motivo-revisione" />
              </div>
            )}

            {/* WYSIWYG */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-xs">Contenuto procedura</Label>
                <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 cursor-pointer transition" title="Carica un PDF: il sistema estrae testo e immagini e popola automaticamente l'editor. Il file PDF non viene salvato.">
                  {importingPdf ? (
                    <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin" />Importazione in corso…</span>
                  ) : (
                    <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Importa da PDF</span>
                  )}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handleImportPdf}
                    disabled={importingPdf}
                    className="hidden"
                    data-testid="import-pdf-input"
                  />
                </label>
              </div>
              <WysiwygEditor value={form.contenuto} onChange={(html) => setForm(prev => ({...prev, contenuto: html}))}
                placeholder="Scrivi il contenuto..." />
            </div>

            {/* Destinatari */}
            <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Destinatari (proposti)</Label>
                <p className="text-[11px] text-gray-500 mt-0.5">Suggerimento di destinatari per la futura pubblicazione. La visibilità effettiva viene scelta al momento della pubblicazione. La procedura resta privata finché non viene pubblicata.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                <CheckboxGroup title="Ruoli" options={RUOLI_OPTS.map(r => ({ value: r.key, label: r.label }))}
                  selected={form.target_roles}
                  onToggle={(v) => setForm(p => ({...p, target_roles: toggleArr(p.target_roles, v)}))}
                  testid="group-roles" />
                <CheckboxGroup title="Figure prof." options={FIGURE_OPTS.map(f => ({ value: f, label: f }))}
                  selected={form.target_figure}
                  onToggle={(v) => setForm(p => ({...p, target_figure: toggleArr(p.target_figure, v)}))}
                  testid="group-figure" />
                <CheckboxGroup title="ASL" options={ASL_OPTS.map(a => ({ value: a, label: a }))}
                  selected={form.target_asl}
                  onToggle={(v) => setForm(p => ({...p, target_asl: toggleArr(p.target_asl, v)}))}
                  testid="group-asl" />
              </div>
            </div>

            {/* Mandatory */}
            <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-cyan-400 transition-colors">
              <input type="checkbox" checked={form.is_mandatory}
                onChange={(e) => setForm({...form, is_mandatory: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                data-testid="checkbox-mandatory" />
              <div>
                <span className="text-sm font-medium text-gray-700">Obbligatoria</span>
                <p className="text-[11px] text-gray-500">Richiede presa visione da parte di tutti i destinatari</p>
              </div>
            </label>

            {/* Documento già approvato (import) - admin */}
            {!editingProc && isAdmin && (
              <div className="space-y-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.import_approvato}
                    onChange={(e) => setForm({...form, import_approvato: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="checkbox-import-approvato" />
                  <span className="text-sm font-medium text-emerald-900">Documento già approvato</span>
                </label>
                <p className="text-[11px] text-emerald-700">Salva il documento come <b>approvato</b> (privato). La pubblicazione avviene dopo, con il pulsante dedicato.</p>
                {form.import_approvato && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-[11px]">Data approvazione *</Label>
                      <Input type="date" value={form.data_approvazione} onChange={(e)=>setForm({...form,data_approvazione:e.target.value})} className="h-9" data-testid="input-data-approvazione" /></div>
                    <div className="space-y-1"><Label className="text-[11px]">Approvatore *</Label>
                      <Input value={form.approvatore} onChange={(e)=>setForm({...form,approvatore:e.target.value})} placeholder="Nome e cognome" className="h-9" data-testid="input-approvatore" /></div>
                    <div className="space-y-1"><Label className="text-[11px]">Ruolo approvatore *</Label>
                      <Input value={form.ruolo_approvatore} onChange={(e)=>setForm({...form,ruolo_approvatore:e.target.value})} placeholder='Es. "Direttore Sanitario"' className="h-9" data-testid="input-ruolo-approvatore" /></div>
                    <div className="space-y-1"><Label className="text-[11px]">Versione *</Label>
                      <Input value={form.versione} onChange={(e)=>setForm({...form,versione:e.target.value})} className="h-9" data-testid="input-versione-approvato" /></div>
                  </div>
                )}
              </div>
            )}

            {/* Verificatori - only for new procedures (non in modalità già-approvato) */}
            {!editingProc && !form.import_approvato && (isEditor || isSuperAdmin) && (
              <div className="space-y-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-cyan-100 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-4 h-4 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-semibold text-cyan-900">Verifica e Approvazione</Label>
                    <p className="text-[11px] text-cyan-700 mt-0.5">
                      {isEditor && !isAdmin
                        ? 'La procedura entrerà in verifica: richiede l\'approvazione di tutti e 3 i verificatori.'
                        : 'Indica i 3 verificatori e il loro ruolo (es. "Direttore Sanitario"). Verranno mostrati anche nel PDF esportato.'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {[0, 1, 2].map(idx => (
                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-2 p-2 bg-white rounded-md border border-cyan-200">
                      <div className="space-y-1">
                        <Label className="text-[11px] text-cyan-800">Verificatore {idx + 1}</Label>
                        <select
                          value={form.verificatori_ids[idx] || ''}
                          onChange={(e) => {
                            const next = [...form.verificatori_ids];
                            next[idx] = e.target.value;
                            // Auto-popola il "Ruolo / Titolo" con la prima qualifica dell'utente
                            // (solo se il campo titolo era vuoto o uguale ad una qualifica precedente)
                            const nextTitoli = [...(form.verificatori_titoli || ['', '', ''])];
                            const selectedUser = availableVerifiers.find(v => v.id === e.target.value);
                            const firstQualifica = selectedUser?.qualifiche?.[0] || '';
                            const prevSelected = availableVerifiers.find(v => v.id === form.verificatori_ids[idx]);
                            const prevAutoFilled = prevSelected?.qualifiche?.[0] || '';
                            if (firstQualifica && (!nextTitoli[idx] || nextTitoli[idx] === prevAutoFilled)) {
                              nextTitoli[idx] = firstQualifica;
                            }
                            setForm({...form, verificatori_ids: next, verificatori_titoli: nextTitoli});
                          }}
                          className="w-full h-9 px-2 text-xs rounded-md border border-cyan-200 bg-white"
                          data-testid={`select-verifier-${idx}`}
                        >
                          <option value="">— Seleziona —</option>
                          {availableVerifiers
                            .filter(v => v.id === form.verificatori_ids[idx] || !form.verificatori_ids.includes(v.id))
                            .map(v => (
                              <option key={v.id} value={v.id}>
                                {v.full_name}{v.qualifiche?.length ? ` · ${v.qualifiche[0]}` : (v.is_redattore ? ' · Redattore' : '')}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-cyan-800">Ruolo / Titolo</Label>
                        <Input
                          value={form.verificatori_titoli?.[idx] || ''}
                          onChange={(e) => {
                            const next = [...(form.verificatori_titoli || ['', '', ''])];
                            next[idx] = e.target.value;
                            setForm({...form, verificatori_titoli: next});
                          }}
                          placeholder='Es. "Direttore Sanitario"'
                          className="h-9 text-xs"
                          maxLength={120}
                          data-testid={`input-verifier-titolo-${idx}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingProc(null); setForm(emptyForm); }}>
              Annulla
            </Button>
            {editingProc ? (
              <Button onClick={() => handleSave('salva_privato')} className="bg-cyan-600 hover:bg-cyan-700" disabled={!form.titolo || !form.tipo} data-testid="btn-save-procedure">
                Salva Modifiche
              </Button>
            ) : form.import_approvato ? (
              <Button onClick={() => handleSave('importa_approvato')} className="bg-emerald-600 hover:bg-emerald-700" disabled={!form.titolo || !form.tipo} data-testid="btn-import-approvato">
                Importa come già approvato
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => handleSave('salva_privato')} disabled={!form.titolo || !form.tipo} data-testid="btn-save-privato">
                  Salva in privato
                </Button>
                <Button onClick={() => handleSave('invia_verifica')} className="bg-cyan-600 hover:bg-cyan-700" disabled={!form.titolo || !form.tipo} data-testid="btn-invia-verifica">
                  Invia in verifica
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-cyan-600" /> Verifica procedura
            </DialogTitle>
            <DialogDescription>
              {selectedProc?.titolo} · <span className="font-mono">{selectedProc?.codice}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Label className="text-xs">Note (opzionali)</Label>
            <textarea
              value={verifyNote}
              onChange={(e) => setVerifyNote(e.target.value)}
              placeholder="Motivazioni, osservazioni, richieste di modifica..."
              className="w-full min-h-[100px] p-3 text-sm rounded-md border border-gray-200 focus:outline-none focus:border-cyan-400"
              data-testid="input-verify-note"
            />
            <p className="text-[11px] text-gray-500">
              Se <b>rifiuti</b>, la procedura torna in bozza e il redattore potrà correggerla.
              Se <b>tutti e 3</b> i verificatori approvano, diventa <b>approvato</b> (privato) e potrà essere pubblicata separatamente.
            </p>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setShowVerifyDialog(false)}>Annulla</Button>
            <Button variant="outline" onClick={() => handleVerify(false)}
              className="border-red-200 text-red-700 hover:bg-red-50 gap-1.5"
              data-testid="btn-reject">
              <XCircle className="w-4 h-4" /> Rifiuta
            </Button>
            <Button onClick={() => handleVerify(true)}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              data-testid="btn-approve">
              <CheckCircle className="w-4 h-4" /> Approva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Dialog (Super Admin) */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent className="max-w-lg" data-testid="publish-dialog">
          <DialogHeader>
            <DialogTitle>Pubblica procedura</DialogTitle>
            <DialogDescription>Scegli chi potrà vedere la procedura. La scelta "Tutti" richiede conferma esplicita.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-2">
              <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${publishForm.visibility === 'targeted' ? 'border-cyan-400 bg-cyan-50' : 'border-gray-200'}`} data-testid="radio-visibility-targeted">
                <input type="radio" name="vis" checked={publishForm.visibility === 'targeted'} onChange={() => setPublishForm(p => ({...p, visibility: 'targeted'}))} className="mt-0.5" />
                <div><div className="text-sm font-medium">Destinatari selezionati</div><div className="text-[11px] text-gray-500">Visibile solo ai ruoli / figure / ASL indicati sotto.</div></div>
              </label>
              <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${publishForm.visibility === 'all' ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'}`} data-testid="radio-visibility-all">
                <input type="radio" name="vis" checked={publishForm.visibility === 'all'} onChange={() => setPublishForm(p => ({...p, visibility: 'all'}))} className="mt-0.5" />
                <div><div className="text-sm font-medium">Tutti gli utenti</div><div className="text-[11px] text-gray-500">Visibile a tutti gli utenti attivi.</div></div>
              </label>
            </div>
            {publishForm.visibility === 'targeted' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <CheckboxGroup title="Ruoli" options={RUOLI_OPTS.map(r => ({ value: r.key, label: r.label }))}
                  selected={publishForm.target_roles}
                  onToggle={(v) => setPublishForm(p => ({...p, target_roles: toggleArr(p.target_roles, v)}))} testid="pub-group-roles" />
                <CheckboxGroup title="Figure prof." options={FIGURE_OPTS.map(f => ({ value: f, label: f }))}
                  selected={publishForm.target_figure}
                  onToggle={(v) => setPublishForm(p => ({...p, target_figure: toggleArr(p.target_figure, v)}))} testid="pub-group-figure" />
                <CheckboxGroup title="ASL" options={ASL_OPTS.map(a => ({ value: a, label: a }))}
                  selected={publishForm.target_asl}
                  onToggle={(v) => setPublishForm(p => ({...p, target_asl: toggleArr(p.target_asl, v)}))} testid="pub-group-asl" />
              </div>
            )}
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>Annulla</Button>
            <Button onClick={handlePublish} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="btn-confirm-publish">
              <CheckCircle className="w-4 h-4" /> Pubblica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to verify Dialog (from a draft) */}
      <Dialog open={showSendVerifyDialog} onOpenChange={setShowSendVerifyDialog}>
        <DialogContent className="max-w-lg" data-testid="send-verify-dialog">
          <DialogHeader>
            <DialogTitle>Invia in verifica</DialogTitle>
            <DialogDescription>Seleziona i 3 verificatori. La procedura richiede l'approvazione di tutti.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {[0, 1, 2].map(idx => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-2 p-2 bg-white rounded-md border border-cyan-200">
                <div className="space-y-1">
                  <Label className="text-[11px] text-cyan-800">Verificatore {idx + 1}</Label>
                  <select value={sendVerifyForm.verificatori_ids[idx] || ''}
                    onChange={(e) => {
                      const next = [...sendVerifyForm.verificatori_ids]; next[idx] = e.target.value;
                      const nextT = [...sendVerifyForm.verificatori_titoli];
                      const su = availableVerifiers.find(v => v.id === e.target.value);
                      if (su?.qualifiche?.[0] && !nextT[idx]) nextT[idx] = su.qualifiche[0];
                      setSendVerifyForm({ verificatori_ids: next, verificatori_titoli: nextT });
                    }}
                    className="w-full h-9 px-2 text-xs rounded-md border border-cyan-200 bg-white" data-testid={`sv-select-verifier-${idx}`}>
                    <option value="">— Seleziona —</option>
                    {availableVerifiers.filter(v => v.id === sendVerifyForm.verificatori_ids[idx] || !sendVerifyForm.verificatori_ids.includes(v.id))
                      .map(v => <option key={v.id} value={v.id}>{v.full_name}{v.qualifiche?.length ? ` · ${v.qualifiche[0]}` : ''}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-cyan-800">Ruolo / Titolo</Label>
                  <Input value={sendVerifyForm.verificatori_titoli?.[idx] || ''}
                    onChange={(e) => { const next = [...sendVerifyForm.verificatori_titoli]; next[idx] = e.target.value; setSendVerifyForm(p => ({...p, verificatori_titoli: next})); }}
                    placeholder='Es. "Direttore Sanitario"' className="h-9 text-xs" maxLength={120} data-testid={`sv-input-titolo-${idx}`} />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setShowSendVerifyDialog(false)}>Annulla</Button>
            <Button onClick={handleSendVerify} className="bg-cyan-600 hover:bg-cyan-700 gap-1.5" data-testid="btn-confirm-send-verify">
              <Send className="w-4 h-4" /> Invia in verifica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Categories Dialog */}
      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gestione Categorie</DialogTitle>
            <DialogDescription>Crea, modifica ed elimina le categorie delle procedure</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex gap-2">
              <Input value={catForm.nome} onChange={(e) => setCatForm({...catForm, nome: e.target.value})}
                placeholder="Nome categoria" className="h-9 flex-1" data-testid="input-cat-nome" />
              <Input type="number" value={catForm.ordine} onChange={(e) => setCatForm({...catForm, ordine: parseInt(e.target.value) || 0})}
                placeholder="Ordine" className="h-9 w-20" data-testid="input-cat-ordine" />
              <Button onClick={handleSaveCat} size="sm" disabled={!catForm.nome}
                className="h-9 bg-cyan-600 hover:bg-cyan-700" data-testid="btn-save-cat">
                {editingCat ? 'Salva' : <Plus className="w-4 h-4" />}
              </Button>
            </div>
            {editingCat && (
              <button onClick={() => { setEditingCat(null); setCatForm({ nome: '', descrizione: '', ordine: 0 }); }}
                className="text-xs text-gray-500 underline">Annulla modifica</button>
            )}
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
              {categories.length === 0 ? (
                <p className="text-center text-xs text-gray-400 py-4">Nessuna categoria</p>
              ) : categories.map(cat => {
                const associated = procedures.filter(p => p.categoria_id === cat.id).length;
                return (
                  <div key={cat.id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{cat.nome}</span>
                        <span className="text-[10px] text-gray-400">#{cat.ordine}</span>
                      </div>
                      <p className="text-[11px] text-gray-500">{associated} procedure associate</p>
                    </div>
                    <div className="flex gap-0.5 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                        onClick={() => { setEditingCat(cat); setCatForm({ nome: cat.nome, descrizione: cat.descrizione || '', ordine: cat.ordine || 0 }); }}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 disabled:opacity-30"
                        disabled={associated > 0}
                        title={associated > 0 ? 'Non puoi eliminare: procedure associate' : 'Elimina'}
                        onClick={() => handleDeleteCat(cat)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------- Subcomponents ------------------- */

function StatCard({ label, value, color, bg }) {
  return (
    <div className={`rounded-lg border border-gray-100 ${bg} p-3`}>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-[11px] text-gray-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
        active ? 'bg-cyan-100 text-cyan-700 border-cyan-200 shadow-sm' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
      }`}>
      {children}
    </button>
  );
}

function MetaField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">{label}</div>
      <div className="text-sm font-medium text-gray-700">{value}</div>
    </div>
  );
}

function CheckboxGroup({ title, options, selected, onToggle, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-[11px] font-semibold text-gray-600">{title}</Label>
      <div className="space-y-1 bg-white rounded-md border border-gray-200 p-2 max-h-48 overflow-y-auto">
        {options.map(o => (
          <label key={o.value} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-gray-50 cursor-pointer">
            <input type="checkbox" checked={selected.includes(o.value)} onChange={() => onToggle(o.value)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500" />
            <span className="text-xs text-gray-700">{o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ProcedureRow({ proc, isAdmin, read, reread, catName, tipoInfo, onView, onEdit, onArchive, onRestore, onDelete, onPDF }) {
  const archived = proc.is_active === false;
  const TipoIcon = tipoInfo?.icon || FileText;
  const workflow = proc.stato_workflow || 'pubblicato';
  const isPub = workflow === 'pubblicato';
  const approvedCount = (proc.verificatori || []).filter(v => v.stato === 'approvato').length;
  const totalVerifiers = (proc.verificatori || []).length;

  let statusClass = 'border-cyan-200 bg-cyan-50/30';
  if (archived) statusClass = 'border-gray-200 bg-gray-50 opacity-70';
  else if (workflow === 'in_verifica') statusClass = 'border-amber-200 bg-amber-50/30';
  else if (workflow === 'bozza') statusClass = 'border-gray-200 bg-gray-50/40';
  else if (workflow === 'approvato') statusClass = 'border-emerald-200 bg-emerald-50/30';
  else if (read && !reread) statusClass = 'border-gray-100 bg-white';
  else if (reread) statusClass = 'border-amber-200 bg-amber-50/20';

  return (
    <div className={`group rounded-lg border transition-all ${statusClass}`} data-testid={`procedure-${proc.id}`}>
      <div className="flex items-stretch gap-3 p-3 cursor-pointer" onClick={onView}>
        {/* Icona tipo */}
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${tipoInfo?.bg || 'bg-gray-100'}`}>
          <TipoIcon className={`w-4 h-4 ${tipoInfo?.color || 'text-gray-500'}`} />
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-mono font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {proc.codice || '—'}
            </span>
            <span className={`text-sm font-semibold truncate ${read && !reread && isPub ? 'text-gray-700' : 'text-gray-900'}`}>
              {proc.titolo}
            </span>
            {workflow === 'in_verifica' && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded flex items-center gap-1">
                <Clock className="w-3 h-3" /> In verifica ({approvedCount}/{totalVerifiers})
              </span>
            )}
            {workflow === 'bozza' && (
              <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-[10px] font-medium rounded">Bozza</span>
            )}
            {workflow === 'approvato' && (
              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-medium rounded flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Approvato (privato)
              </span>
            )}
            {isPub && !read && !archived && <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 text-[10px] font-medium rounded">Da leggere</span>}
            {isPub && reread && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded">Aggiornata</span>}
            {proc.is_mandatory && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] font-medium rounded">Obbligatoria</span>}
            {archived && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 text-[10px] font-medium rounded">Archiviata</span>}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">{catName}</span>
            {tipoInfo && (
              <span className={`px-1.5 py-0.5 ${tipoInfo.bg} ${tipoInfo.color} rounded text-[10px] font-medium`}>
                {tipoInfo.label}
              </span>
            )}
            <span className="text-[11px]">v{proc.versione}</span>
            <span className="text-[11px]">
              {isPub ? 'Pubblicata' : workflow === 'in_verifica' ? 'Creata' : workflow === 'approvato' ? 'Approvata' : 'Bozza'}:{' '}
              {proc.created_at ? new Date(proc.created_at).toLocaleDateString('it-IT') : '—'}
            </span>
            {isAdmin && isPub && <span className="text-[11px] text-cyan-600">{proc.reads?.length || 0} letture</span>}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-0.5 shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700" onClick={onView} title="Visualizza">
            <Eye className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700" onClick={onPDF} title="Scarica PDF">
            <Download className="w-3.5 h-3.5" />
          </Button>
          {isAdmin && !archived && (
            <>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700" onClick={onEdit} title="Modifica">
                <Edit className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-amber-600" onClick={onArchive} title="Archivia">
                <Archive className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
          {isAdmin && archived && (
            <>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-emerald-600" onClick={onRestore} title="Ripristina">
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-red-600" onClick={onDelete} title="Elimina definitivamente">
                <X className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
